package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.success
import com.siriusxm.pia.CapabilityAction
import com.siriusxm.pia.CapabilityQuery
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.unifiedcontent.console.ucp.ConsumerPolicySyncClient
import com.siriusxm.unifiedcontent.console.ucp.PolicySyncChange
import com.siriusxm.unifiedcontent.console.ucp.PolicySyncStep
import com.siriusxm.unifiedcontent.console.ucp.desiredConsumerPolicies
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import unifiedcontent.aggregator.ConsumerDetails

/** UCP's own action for authoring policies on a service's actions, scoped by `serviceId`. */
private val ManagePoliciesCapability = CapabilityAction("UnifiedContentPermissions_ManagePolicies")

/**
 * Whether the viewer may manage policies on the aggregator's actions in UCP, so the Team box can
 * offer Sync Permissions. Reads false until known, and stays false when UCP or AVP cannot answer.
 * Hide-only: UCP checks the same action on every policy write.
 */
@Composable
internal fun rememberCanSyncConsumerPermissions(aggregator: AggregatorService, teamId: String?): Boolean {
    var allowed by remember(teamId) { mutableStateOf(false) }
    LaunchedEffect(teamId) {
        allowed = false
        if (teamId.isNullOrBlank()) return@LaunchedEffect
        allowed = try {
            val client = ConsumerPolicySyncClient(aggregator.context, aggregator.context.configuration.ucpApiUrl)
            val serviceId = client.aggregatorServiceId()
            aggregator.context.capabilities.isAuthorized(
                CapabilityQuery(ManagePoliciesCapability, context = buildJsonObject { put("serviceId", serviceId) })
            )
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Unable to check whether the viewer can manage aggregator policies", e)
            false
        }
    }
    return allowed
}

/**
 * The Sync Permissions dialog (UCA-1240). It grants the consumer's UCP team the aggregator actions it
 * needs for its own targets (target administration, backfill and replay), each scoped to this
 * consumer and its configured AWS accounts, and shows the plan before writing anything.
 *
 * Re-running it is safe: policies it created earlier are updated in place (for example after an
 * account is added), and grants written by hand are never changed or removed. A consumer with no
 * configured accounts cannot be synced, because the account filter would have nothing to match.
 */
@Composable
internal fun SyncConsumerPermissionsDialog(
    aggregator: AggregatorService,
    consumer: ConsumerDetails,
    teamId: String,
    onClose: () -> Unit
) {
    val client = remember(aggregator) {
        ConsumerPolicySyncClient(aggregator.context, aggregator.context.configuration.ucpApiUrl)
    }
    val desired = remember(consumer.id, consumer.accounts) {
        desiredConsumerPolicies(consumer.id, consumer.accounts.orEmpty())
    }
    var plan by remember { mutableStateOf<List<PolicySyncStep>?>(null) }
    var planError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(teamId, desired) {
        plan = null
        planError = null
        if (desired == null) return@LaunchedEffect
        try {
            plan = client.plan(teamId, desired)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            planError = e.message ?: "UCP could not be reached."
        }
    }

    val pending = plan?.filter { it.change != PolicySyncChange.UNCHANGED }.orEmpty()

    dialogOverlay {
        title = "Sync Permissions"
        closeActionTitle = "Cancel"
        onClose { onClose() }
        action {
            primary = true
            title = "Sync"
            enabled = pending.isNotEmpty()
            showProgressOnAction = true
            action {
                try {
                    client.apply(teamId, pending)
                    aggregator.notifications.success(
                        "Permissions synced",
                        "UCP applies new policies within about a minute."
                    )
                    onClose()
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Throwable) {
                    aggregator.notifications.error("Unable to sync permissions: ${e.message ?: "unknown error"}")
                }
            }
        }
        content {
            when {
                desired == null -> messageBox(
                    "Configure this consumer's AWS accounts before syncing. Permissions are scoped to those accounts.",
                    MessageType.WARNING
                )
                planError != null -> messageBox("Unable to read the team's policies: $planError", MessageType.ALERT)
                plan == null -> boxSpinner()
                else -> {
                    messageBox(
                        "Grants this consumer's team the following, limited to consumer ${consumer.id} and " +
                                "accounts ${desired.first().accountIds.joinToString(", ")}. " +
                                "API Destination targets are not included. Grants created by hand are left as they are."
                    )
                    Ul {
                        plan.orEmpty().forEach { step ->
                            Li {
                                Text("${step.desired.actionId.removePrefix("UnifiedContentAggregator_")}: ${step.change.label}")
                            }
                        }
                    }
                }
            }
        }
    }
}

private val PolicySyncChange.label: String
    get() = when (this) {
        PolicySyncChange.CREATE -> "will be created"
        PolicySyncChange.UPDATE -> "will be updated"
        PolicySyncChange.UNCHANGED -> "already up to date"
    }
